import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  IconButton,
  Dialog as FullImageDialog,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const EditDialog = ({
  open,
  onClose,
  onSubmit,
  title,
  fields,
  formData,
  setFormData,
  videoUrlKey,
}) => {
  const [fullImageOpen, setFullImageOpen] = useState(false);
  const [fullImageSrc, setFullImageSrc] = useState("");

  const handleFieldChange = (key, value) => {
    // Handle "currently working" checkbox logic - only allow one to be checked
    if (key.startsWith("currentlyWorking_") && value === true) {
      const updatedFormData = { ...formData };
      // Uncheck all other "currently working" checkboxes
      Object.keys(updatedFormData).forEach((formKey) => {
        if (formKey.startsWith("currentlyWorking_") && formKey !== key) {
          updatedFormData[formKey] = false;
        }
      });
      updatedFormData[key] = value;
      setFormData(updatedFormData);
    } else {
      setFormData({ ...formData, [key]: value });
    }
  };

  const handleVideoUrlChange = (value, index) => {
    const updated = [...formData[videoUrlKey]];
    updated[index] = value;
    setFormData({ ...formData, [videoUrlKey]: updated });
  };

  const handleAddVideoUrl = () => {
    setFormData({
      ...formData,
      [videoUrlKey]: [...formData[videoUrlKey], ""],
    });
  };

  const handleRemoveVideoUrl = (index) => {
    const updated = formData[videoUrlKey].filter((_, i) => i !== index);
    setFormData({
      ...formData,
      [videoUrlKey]: updated.length > 0 ? updated : [""],
    });
  };

  const handleImageChange = (key, event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        handleFieldChange(key, e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleViewImage = (src) => {
    setFullImageSrc(src);
    setFullImageOpen(true);
  };

  const handleCloseFullImage = () => {
    setFullImageOpen(false);
    setFullImageSrc("");
  };

  const groupedFields = fields.reduce((acc, field) => {
    const group = field.group || "ungrouped";
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(field);
    return acc;
  }, {});

  const imageField = fields.find((field) => field.type === "image");
  const nameField = fields.find((field) => field.key === "fullName" || field.key === "companyName");
  const emailField = fields.find(
    (field) => field.key === "personalEmail" || field.key === "companyEmail"
  );
  const otherFields = fields.filter(
    (field) =>
      field !== imageField &&
      field !== nameField &&
      field !== emailField &&
      field.type !== "checkbox" &&
      field.type !== "hidden" &&
      !field.group
  );
  const checkboxFields = fields.filter((field) => field.type === "checkbox" && !field.group);

  const orderedFields = [
    ...(imageField ? [imageField] : []),
    ...(nameField ? [nameField] : []),
    ...(emailField ? [emailField] : []),
    ...otherFields,
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 600}
        sx={{
          "& .MuiDialog-paper": {
            margin: { xs: 0.5, sm: 2 },
            maxHeight: { xs: "calc(100% - 8px)", sm: "calc(100% - 64px)" },
            width: { xs: "calc(100% - 8px)", sm: "100%" },
            maxWidth: { xs: "calc(100% - 8px)", sm: "600px" },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: { xs: 1, sm: 2 },
          }}
        >
          {title}
          {window.innerWidth < 600 && (
            <IconButton
              onClick={onClose}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              ✕
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            px: { xs: 1.5, sm: 3 },
            py: { xs: 1, sm: 2 },
            maxHeight: { xs: "calc(100vh - 160px)", sm: "70vh" },
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "3px",
              "&:hover": {
                background: "#a8a8a8",
              },
            },
          }}
        >
          <MDBox
            display="flex"
            flexDirection="column"
            gap={{ xs: 3, sm: 3.5 }}
            sx={{
              "& .MuiTextField-root": {
                marginBottom: { xs: "4px", sm: "8px" },
                "& .MuiInputBase-root": {
                  minHeight: { xs: "52px", sm: "56px" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "14px", sm: "16px" },
                  "&.MuiInputLabel-shrunk": {
                    transform: { xs: "translate(14px, -9px) scale(0.75)", sm: "translate(14px, -9px) scale(0.75)" },
                  },
                },
                "& .MuiOutlinedInput-root": {
                  "& input": {
                    padding: { xs: "14px 14px", sm: "16px 14px" },
                    fontSize: { xs: "14px", sm: "16px" },
                  },
                  "& textarea": {
                    padding: { xs: "14px 14px", sm: "16px 14px" },
                    fontSize: { xs: "14px", sm: "16px" },
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  "& legend": {
                    fontSize: { xs: "0.75em", sm: "0.75em" },
                  },
                },
              },
              "& .MuiFormControlLabel-root": {
                marginBottom: { xs: "4px", sm: "8px" },
                marginTop: { xs: "4px", sm: "8px" },
                "& .MuiCheckbox-root": {
                  padding: { xs: "8px", sm: "9px" },
                },
                "& .MuiTypography-root": {
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                },
              },
            }}
          >
            {orderedFields.map(({ label, key, type, options, inputProps, required, disabled }) => {
              const handleChange = (value) => handleFieldChange(key, value);

              if (type === "select") {
                // Check if this field is marked as multiple in the field definition
                const fieldDef = fields.find((f) => f.key === key);
                const isMultiple = fieldDef?.multiple === true;

                return (
                  <TextField
                    key={key}
                    select
                    label={label}
                    value={isMultiple ? formData[key] || [] : formData[key] ?? ""}
                    onChange={(e) => handleChange(e.target.value)}
                    fullWidth
                    variant="outlined"
                    required={required}
                    disabled={disabled}
                    SelectProps={{
                      multiple: isMultiple,
                      MenuProps: {
                        PaperProps: {
                          style: {
                            maxHeight: 240,
                            backgroundColor: "#fff",
                          },
                        },
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        minHeight: { xs: "52px", sm: "56px" },
                        "& .MuiSelect-select": {
                          padding: { xs: "14px 32px 14px 14px", sm: "16px 32px 16px 14px" },
                          fontSize: { xs: "14px", sm: "16px" },
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "14px", sm: "16px" },
                        "&.MuiInputLabel-shrunk": {
                          transform: { xs: "translate(14px, -9px) scale(0.75)", sm: "translate(14px, -9px) scale(0.75)" },
                        },
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        "& legend": {
                          fontSize: { xs: "0.75em", sm: "0.75em" },
                        },
                      },
                    }}
                  >
                    {options.map((option) => {
                      const optionValue = typeof option === "object" ? option.value : option;
                      const optionLabel = typeof option === "object" ? option.label : option;
                      return (
                        <MenuItem
                          key={optionValue}
                          value={optionValue}
                          sx={{
                            height: "40px",
                            fontSize: "16px",
                            "&:hover": {
                              backgroundColor: "#e3f2fd",
                            },
                          }}
                        >
                          {optionLabel}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                );
              }

              if (type === "time") {
                return (
                  <TextField
                    key={key}
                    label={label}
                    type="time"
                    value={formData[key] ?? ""}
                    onChange={(e) => handleChange(e.target.value)}
                    fullWidth
                    variant="outlined"
                    required={required}
                    disabled={disabled}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        minHeight: { xs: "52px", sm: "56px" },
                        "& input": {
                          padding: { xs: "14px 14px", sm: "16px 14px" },
                          fontSize: { xs: "14px", sm: "16px" },
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "14px", sm: "16px" },
                        "&.MuiInputLabel-shrunk": {
                          transform: { xs: "translate(14px, -9px) scale(0.75)", sm: "translate(14px, -9px) scale(0.75)" },
                        },
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        "& legend": {
                          fontSize: { xs: "0.75em", sm: "0.75em" },
                        },
                      },
                    }}
                  />
                );
              }

              if (type === "date") {
                return (
                  <TextField
                    key={key}
                    label={label}
                    type="date"
                    value={formData[key] ?? ""}
                    onChange={(e) => handleChange(e.target.value)}
                    fullWidth
                    variant="outlined"
                    required={required}
                    disabled={disabled}
                    inputProps={inputProps}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        minHeight: { xs: "52px", sm: "56px" },
                        "& input": {
                          padding: { xs: "14px 14px", sm: "16px 14px" },
                          fontSize: { xs: "14px", sm: "16px" },
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "14px", sm: "16px" },
                        "&.MuiInputLabel-shrunk": {
                          transform: { xs: "translate(14px, -9px) scale(0.75)", sm: "translate(14px, -9px) scale(0.75)" },
                        },
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        "& legend": {
                          fontSize: { xs: "0.75em", sm: "0.75em" },
                        },
                      },
                    }}
                  />
                );
              }

              if (type === "image") {
                return (
                  <MDBox
                    key={key}
                    display="flex"
                    flexDirection="column"
                    gap={1.5}
                    p={2}
                    bgcolor="#f5f5f5"
                    borderRadius="8px"
                    border="1px solid #e0e0e0"
                  >
                    <MDTypography variant="body2" fontWeight="medium">
                      {label}
                    </MDTypography>
                    <MDBox
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      gap={3}
                      flexWrap="wrap"
                    >
                      {formData[key] ? (
                        <img
                          src={formData[key]}
                          alt={label}
                          width={100}
                          height={100}
                          style={{
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "1px solid #e0e0e0",
                          }}
                        />
                      ) : (
                        <MDBox
                          width={100}
                          height={100}
                          bgcolor="grey.200"
                          borderRadius="8px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          border="1px dashed #bdbdbd"
                        >
                          <MDTypography variant="caption" color="text.secondary">
                            No Image
                          </MDTypography>
                        </MDBox>
                      )}
                      <MDBox
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        gap={2}
                        flexShrink={0}
                      >
                        {/* <Button
                          variant="outlined"
                          component="label"
                          startIcon={<AddPhotoAlternateIcon />}
                          sx={{
                            height: "40px",
                            minWidth: "140px",
                            borderColor: "#1976d2",
                            color: "#1976d2",
                            padding: "8px 16px",
                            "&:hover": {
                              borderColor: "#1565c0",
                              backgroundColor: "#e3f2fd",
                            },
                          }}
                        >
                          Upload Image
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={(e) => handleImageChange(key, e)}
                          />
                        </Button> */}
                        {formData[key] && (
                          <>
                            <Button
                              variant="outlined"
                              startIcon={<VisibilityIcon />}
                              sx={{
                                height: "40px",
                                minWidth: "100px",
                                borderColor: "#1976d2",
                                color: "#1976d2",
                                padding: "8px 16px",
                                "&:hover": {
                                  borderColor: "#1565c0",
                                  backgroundColor: "#e3f2fd",
                                },
                              }}
                              onClick={() => handleViewImage(formData[key])}
                            >
                              View
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              sx={{
                                height: "40px",
                                minWidth: "100px",
                                padding: "8px 16px",
                                borderColor: "#d32f2f",
                                color: "#d32f2f",
                                "&:hover": {
                                  borderColor: "#b71c1c",
                                  backgroundColor: "#ffebee",
                                },
                              }}
                              onClick={() => handleFieldChange(key, "")}
                            >
                              Remove
                            </Button>
                          </>
                        )}
                      </MDBox>
                    </MDBox>
                  </MDBox>
                );
              }

              if (key === "resume" && type === "text") {
                return (
                  <MDBox
                    key={key}
                    display="flex"
                    flexDirection="column"
                    gap={1.5}
                    p={2}
                    bgcolor="#f5f5f5"
                    borderRadius="8px"
                    border="1px solid #e0e0e0"
                  >
                    <MDTypography variant="body2" fontWeight="normal">
                      {label}
                    </MDTypography>
                    {formData[key] ? (
                      <MDBox
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        gap={2}
                        flexWrap="wrap"
                      >
                        <MDBox
                          display="flex"
                          alignItems="center"
                          gap={1}
                          p={1}
                          bgcolor="white"
                          borderRadius="4px"
                          border="1px solid #e0e0e0"
                          width="fit-content"
                        >
                          <InsertDriveFileIcon color="action" />
                          <MDTypography variant="caption" color="text.secondary">
                            Resume Document
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          gap={2}
                          flexShrink={0}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            sx={{
                              height: "40px",
                              minWidth: "100px",
                              borderColor: "#1976d2",
                              color: "#1976d2",
                              padding: "8px 16px",
                              "&:hover": {
                                borderColor: "#1565c0",
                                backgroundColor: "#e3f2fd",
                              },
                            }}
                            onClick={() => window.open(formData[key], "_blank")}
                          >
                            View
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            sx={{
                              height: "40px",
                              minWidth: "100px",
                              borderColor: "#1976d2",
                              color: "#1976d2",
                              padding: "8px 16px",
                              "&:hover": {
                                borderColor: "#1565c0",
                                backgroundColor: "#e3f2fd",
                              },
                            }}
                            onClick={() => {
                              const link = document.createElement("a");
                              link.href = formData[key];
                              link.download = "resume";
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                          >
                            Download
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            sx={{
                              height: "40px",
                              minWidth: "100px",
                              padding: "8px 16px",
                              borderColor: "#d32f2f",
                              color: "#d32f2f",
                              "&:hover": {
                                borderColor: "#b71c1c",
                                backgroundColor: "#ffebee",
                              },
                            }}
                            onClick={() => handleFieldChange(key, "")}
                          >
                            Remove
                          </Button>
                        </MDBox>
                      </MDBox>
                    ) : (
                      <TextField
                        label="Resume URL"
                        type="text"
                        value={formData[key] ?? ""}
                        onChange={(e) => handleChange(e.target.value)}
                        fullWidth
                        variant="outlined"
                        required={required}
                        disabled={disabled}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            minHeight: { xs: "52px", sm: "56px" },
                            "& input": {
                              padding: { xs: "14px 14px", sm: "16px 14px" },
                              fontSize: { xs: "14px", sm: "16px" },
                            },
                          },
                          "& .MuiInputLabel-root": {
                            fontSize: { xs: "14px", sm: "16px" },
                            "&.MuiInputLabel-shrunk": {
                              transform: { xs: "translate(14px, -9px) scale(0.75)", sm: "translate(14px, -9px) scale(0.75)" },
                            },
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            "& legend": {
                              fontSize: { xs: "0.75em", sm: "0.75em" },
                            },
                          },
                        }}
                      />
                    )}
                  </MDBox>
                );
              }

              return (
                <TextField
                  key={key}
                  label={label}
                  type={type}
                  value={formData[key] ?? ""}
                  onChange={(e) =>
                    handleChange(type === "number" ? parseInt(e.target.value) || 0 : e.target.value)
                  }
                  fullWidth
                  multiline={type === "textarea"}
                  rows={type === "textarea" ? 3 : 1}
                  variant="outlined"
                  required={required}
                  disabled={disabled}
                  inputProps={inputProps}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: type === "textarea" ? "auto" : { xs: "52px", sm: "56px" },
                      "& input": {
                        padding: { xs: "14px 14px", sm: "16px 14px" },
                        fontSize: { xs: "14px", sm: "16px" },
                      },
                      "& textarea": {
                        padding: { xs: "14px 14px", sm: "16px 14px" },
                        fontSize: { xs: "14px", sm: "16px" },
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: "14px", sm: "16px" },
                      "&.MuiInputLabel-shrunk": {
                        transform: { xs: "translate(14px, -9px) scale(0.75)", sm: "translate(14px, -9px) scale(0.75)" },
                      },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      "& legend": {
                        fontSize: { xs: "0.75em", sm: "0.75em" },
                      },
                    },
                  }}
                />
              );
            })}

            {["personal", "subscription", "education", "experience", "preferred", "company", "profile", "admin"]
              .filter((group) => groupedFields[group] && groupedFields[group].length > 0)
              .map((group) => (
                <MDBox
                  key={group}
                  mt={{ xs: 2, sm: 3 }}
                  sx={{
                    borderTop: "1px solid #e0e0e0",
                    pt: { xs: 2, sm: 2.5 },
                  }}
                >
                  <MDTypography
                    variant="subtitle2"
                    mb={{ xs: 1.5, sm: 2 }}
                    fontWeight="medium"
                    sx={{
                      color: "text.primary",
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                  >
                    {group === "profile" ? "Profile Settings" :
                     group === "personal" ? "Personal Details" :
                     group === "subscription" ? "Subscription Plan" :
                     group === "company" ? "Company Details" :
                     group === "education" ? "Education" :
                     group === "experience" ? "Experiences" :
                     group === "preferred" ? "Preferred Settings" :
                     group.charAt(0).toUpperCase() + group.slice(1) + " Details"}
                  </MDTypography>
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    gap={{ xs: 2, sm: 2.5 }}
                  >
                    {groupedFields[group].map(
                      ({ label, key, type, options, inputProps, required, disabled }) => {
                        const handleChange = (value) => handleFieldChange(key, value);

                        if (type === "select") {
                          // Check if this field is marked as multiple in the field definition
                          const fieldDef = fields.find((f) => f.key === key);
                          const isMultiple = fieldDef?.multiple === true;

                          return (
                            <TextField
                              key={key}
                              select
                              label={label}
                              value={isMultiple ? formData[key] || [] : formData[key] ?? ""}
                              onChange={(e) => handleChange(e.target.value)}
                              fullWidth
                              variant="outlined"
                              required={required}
                              disabled={disabled}
                              SelectProps={{
                                multiple: isMultiple,
                                MenuProps: {
                                  PaperProps: {
                                    style: {
                                      maxHeight: 240,
                                      backgroundColor: "#fff",
                                    },
                                  },
                                },
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  minHeight: "48px",
                                  "& .MuiSelect-select": {
                                    padding: "12px 32px 12px 14px",
                                    fontSize: "16px",
                                  },
                                },
                                "& .MuiInputLabel-root": {
                                  top: "-2px",
                                  fontSize: "16px",
                                },
                              }}
                            >
                              {options.map((option) => {
                                const optionValue =
                                  typeof option === "object" ? option.value : option;
                                const optionLabel =
                                  typeof option === "object" ? option.label : option;
                                return (
                                  <MenuItem
                                    key={optionValue}
                                    value={optionValue}
                                    sx={{
                                      height: "40px",
                                      fontSize: "16px",
                                      "&:hover": {
                                        backgroundColor: "#e3f2fd",
                                      },
                                    }}
                                  >
                                    {optionLabel}
                                  </MenuItem>
                                );
                              })}
                            </TextField>
                          );
                        }

                        if (type === "time") {
                          return (
                            <TextField
                              key={key}
                              label={label}
                              type="time"
                              value={formData[key] ?? ""}
                              onChange={(e) => handleChange(e.target.value)}
                              fullWidth
                              variant="outlined"
                              required={required}
                              disabled={disabled}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  height: "48px",
                                  "& input": {
                                    padding: "12px 14px",
                                    fontSize: "16px",
                                  },
                                },
                                "& .MuiInputLabel-root": {
                                  top: "-2px",
                                  fontSize: "16px",
                                },
                              }}
                            />
                          );
                        }

                        if (type === "date") {
                          return (
                            <TextField
                              key={key}
                              label={label}
                              type="date"
                              value={formData[key] ?? ""}
                              onChange={(e) => handleChange(e.target.value)}
                              fullWidth
                              variant="outlined"
                              required={required}
                              disabled={disabled}
                              inputProps={inputProps}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  height: "48px",
                                  "& input": {
                                    padding: "12px 14px",
                                    fontSize: "16px",
                                  },
                                },
                                "& .MuiInputLabel-root": {
                                  top: "-2px",
                                  fontSize: "16px",
                                },
                              }}
                            />
                          );
                        }

                        if (type === "image") {
                          return (
                            <MDBox
                              key={key}
                              display="flex"
                              flexDirection="column"
                              gap={1.5}
                              p={2}
                              bgcolor="#f5f5f5"
                              borderRadius="8px"
                              border="1px solid #e0e0e0"
                            >
                              <MDTypography variant="body2" fontWeight="medium">
                                {label}
                              </MDTypography>
                              <MDBox
                                display="flex"
                                flexDirection="row"
                                alignItems="center"
                                gap={3}
                                flexWrap="wrap"
                              >
                                {formData[key] ? (
                                  <img
                                    src={formData[key]}
                                    alt={label}
                                    width={100}
                                    height={100}
                                    style={{
                                      objectFit: "cover",
                                      borderRadius: "8px",
                                      border: "1px solid #e0e0e0",
                                    }}
                                  />
                                ) : (
                                  <MDBox
                                    width={100}
                                    height={100}
                                    bgcolor="grey.200"
                                    borderRadius="8px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    border="1px dashed #bdbdbd"
                                  >
                                    <MDTypography variant="caption" color="text.secondary">
                                      No Image
                                    </MDTypography>
                                  </MDBox>
                                )}
                                <MDBox
                                  display="flex"
                                  flexDirection="row"
                                  alignItems="center"
                                  gap={2}
                                  flexShrink={0}
                                >
                                  {/* <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<AddPhotoAlternateIcon />}
                                    sx={{
                                      height: "40px",
                                      minWidth: "140px",
                                      borderColor: "#1976d2",
                                      color: "#1976d2",
                                      padding: "8px 16px",
                                      "&:hover": {
                                        borderColor: "#1565c0",
                                        backgroundColor: "#e3f2fd",
                                      },
                                    }}
                                  >
                                    Upload Image
                                    <input
                                      type="file"
                                      accept="image/*"
                                      hidden
                                      onChange={(e) => handleImageChange(key, e)}
                                    />
                                  </Button> */}
                                  {formData[key] && (
                                    <>
                                      <Button
                                        variant="outlined"
                                        startIcon={<VisibilityIcon />}
                                        sx={{
                                          height: "40px",
                                          minWidth: "100px",
                                          borderColor: "#1976d2",
                                          color: "#1976d2",
                                          padding: "8px 16px",
                                          "&:hover": {
                                            borderColor: "#1565c0",
                                            backgroundColor: "#e3f2fd",
                                          },
                                        }}
                                        onClick={() => handleViewImage(formData[key])}
                                      >
                                        View
                                      </Button>
                                      <Button
                                        variant="outlined"
                                        color="error"
                                        sx={{
                                          height: "40px",
                                          minWidth: "100px",
                                          padding: "8px 16px",
                                          borderColor: "#d32f2f",
                                          color: "#d32f2f",
                                          "&:hover": {
                                            borderColor: "#b71c1c",
                                            backgroundColor: "#ffebee",
                                          },
                                        }}
                                        onClick={() => handleFieldChange(key, "")}
                                      >
                                        Remove
                                      </Button>
                                    </>
                                  )}
                                </MDBox>
                              </MDBox>
                            </MDBox>
                          );
                        }

                        if (type === "checkbox") {
                          return (
                            <FormControlLabel
                              key={key}
                              control={
                                <Checkbox
                                  checked={!!formData[key]}
                                  onChange={(e) => handleChange(e.target.checked)}
                                  size="small"
                                  sx={{
                                    color: "#1976d2",
                                    "&.Mui-checked": {
                                      color: "#1976d2",
                                    },
                                  }}
                                />
                              }
                              label={<MDTypography variant="body2">{label}</MDTypography>}
                            />
                          );
                        }

                        return (
                          <TextField
                            key={key}
                            label={label}
                            type={type}
                            value={formData[key] ?? ""}
                            onChange={(e) =>
                              handleChange(
                                type === "number" ? parseInt(e.target.value) || 0 : e.target.value
                              )
                            }
                            fullWidth
                            multiline={type === "textarea"}
                            rows={type === "textarea" ? 3 : 1}
                            variant="outlined"
                            required={required}
                            disabled={disabled}
                            inputProps={inputProps}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                minHeight: type === "textarea" ? "auto" : { xs: "52px", sm: "56px" },
                                "& input": {
                                  padding: { xs: "14px 14px", sm: "16px 14px" },
                                  fontSize: { xs: "14px", sm: "16px" },
                                },
                                "& textarea": {
                                  padding: { xs: "14px 14px", sm: "16px 14px" },
                                  fontSize: { xs: "14px", sm: "16px" },
                                },
                              },
                              "& .MuiInputLabel-root": {
                                fontSize: { xs: "14px", sm: "16px" },
                                "&.MuiInputLabel-shrunk": {
                                  transform: { xs: "translate(14px, -9px) scale(0.75)", sm: "translate(14px, -9px) scale(0.75)" },
                                },
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                "& legend": {
                                  fontSize: { xs: "0.75em", sm: "0.75em" },
                                },
                              },
                            }}
                          />
                        );
                      }
                    )}
                  </MDBox>
                </MDBox>
              ))}

            {checkboxFields.length > 0 && (
              <MDBox
                mt={{ xs: 2, sm: 3 }}
                sx={{
                  borderTop: "1px solid #e0e0e0",
                  pt: { xs: 2, sm: 2.5 },
                }}
              >
                <MDTypography
                  variant="subtitle2"
                  mb={{ xs: 1.5, sm: 2 }}
                  fontWeight="medium"
                  sx={{
                    color: "text.primary",
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  }}
                >
                  Profile Settings
                </MDTypography>
                <MDBox
                  display="flex"
                  flexDirection={{ xs: "column", sm: "row" }}
                  flexWrap="wrap"
                  gap={{ xs: 1, sm: 2 }}
                >
                  {checkboxFields.map(({ label, key }) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={!!formData[key]}
                          onChange={(e) => handleFieldChange(key, e.target.checked)}
                          size="small"
                          sx={{
                            color: "#1976d2",
                            "&.Mui-checked": {
                              color: "#1976d2",
                            },
                            padding: { xs: "8px", sm: "9px" },
                          }}
                        />
                      }
                      label={
                        <MDTypography
                          variant="body2"
                          sx={{
                            fontSize: { xs: "0.875rem", sm: "1rem" },
                          }}
                        >
                          {label}
                        </MDTypography>
                      }
                      sx={{
                        margin: 0,
                        width: { xs: "100%", sm: "auto" },
                      }}
                    />
                  ))}
                </MDBox>
              </MDBox>
            )}

            {videoUrlKey && (
              <>
                <MDTypography variant="subtitle2" mt={2}>
                  Video URLs
                </MDTypography>
                {formData[videoUrlKey].map((url, idx) => (
                  <MDBox key={idx} display="flex" gap={1} alignItems="center" mb={1}>
                    <TextField
                      value={url}
                      onChange={(e) => handleVideoUrlChange(e.target.value, idx)}
                      fullWidth
                      variant="outlined"
                      placeholder={`Video URL ${idx + 1}`}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "48px",
                          "& input": {
                            padding: "12px 14px",
                            fontSize: "16px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          top: "-2px",
                          fontSize: "16px",
                        },
                      }}
                    />
                    <IconButton
                      onClick={() => handleRemoveVideoUrl(idx)}
                      disabled={formData[videoUrlKey].length === 1}
                      color="error"
                    >
                      <AddPhotoAlternateIcon fontSize="small" />
                    </IconButton>
                  </MDBox>
                ))}
                <Button
                  onClick={handleAddVideoUrl}
                  variant="outlined"
                  size="small"
                  sx={{
                    mt: 1,
                    height: "40px",
                    borderColor: "#1976d2",
                    color: "#1976d2",
                    "&:hover": {
                      borderColor: "#1565c0",
                      backgroundColor: "#e3f2fd",
                    },
                  }}
                >
                  Add Video URL
                </Button>
              </>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            gap: { xs: 1, sm: 1.5 },
            flexDirection: { xs: "column-reverse", sm: "row" },
          }}
        >
          <Button
            onClick={onClose}
            color="secondary"
            fullWidth={window.innerWidth < 600}
            sx={{
              height: { xs: "44px", sm: "40px" },
              color: "#757575",
              fontSize: { xs: "0.875rem", sm: "0.875rem" },
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            color="primary"
            variant="contained"
            fullWidth={window.innerWidth < 600}
            sx={{
              height: { xs: "44px", sm: "40px" },
              backgroundColor: "#1976d2",
              color: "#ffffff",
              fontSize: { xs: "0.875rem", sm: "0.875rem" },
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Full Image View Dialog */}
      <FullImageDialog open={fullImageOpen} onClose={handleCloseFullImage} maxWidth="md">
        <DialogContent>
          <img
            src={fullImageSrc}
            alt="Full view"
            style={{
              width: "100%",
              maxHeight: "80vh",
              objectFit: "contain",
              borderRadius: "8px",
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseFullImage}
            color="primary"
            variant="contained"
            sx={{
              height: "40px",
              backgroundColor: "#1976d2",
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </FullImageDialog>
    </>
  );
};

EditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      key: PropTypes.string.isRequired,
      type: PropTypes.oneOf([
        "text",
        "number",
        "select",
        "checkbox",
        "image",
        "datetime-local",
        "date",
        "textarea",
        "time",
        "email",
      ]),
      options: PropTypes.array,
      multiple: PropTypes.bool,
      group: PropTypes.string,
      inputProps: PropTypes.object,
      required: PropTypes.bool,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  videoUrlKey: PropTypes.string,
};

export default EditDialog;
